/**
 * Canonical ATP routes under the /atp/* path prefix.
 *
 * These mirror the embed-side paths that Vue sends as child paths in
 * host:init / host:set-location messages (e.g. "/atp/exposed-accounts").
 * They reuse the same page components as the DRP ATP routes but live under
 * the path prefix Angular receives when embedded in the Vue host shell.
 *
 * Canonical ATP routes used in both standalone and host-bridge embedded mode.
 */
import { Routes } from '@angular/router';
import { AsmAuthority } from '@asm/components-core/models/asm-authority';
import { AsmRouteId } from '@asm/components-core/models/asm-route-id';
import { RouterTools } from '@sol/components';

export const routesAtp: Routes = [
  // /atp → /atp/exposed-accounts
  RouterTools.ofRedirect(AsmRouteId.atp, AsmRouteId.atp_exposedAccounts),

  // /atp/exposed-accounts
  RouterTools.ofLazy(
    AsmRouteId.atp_exposedAccounts,
    () =>
      import('@asm/components-atp/components/atp/asm-atp-exposed-accounts-page/asm-atp-exposed-accounts-page.component').then(
        m => m.AsmAtpExposedAccountsPageComponent,
      ),
    [AsmAuthority.MODULE_ACCOUNT_TAKEOVER_PREVENTION, AsmAuthority.VIEW_ATP],
  ),

  // /atp/exposed-accounts/:id → /atp/exposed-accounts/:id/overview
  RouterTools.ofRedirect(AsmRouteId.atp_exposedAccounts_detail, AsmRouteId.atp_exposedAccounts_detail_overview),

  // /atp/exposed-accounts/:id/overview
  RouterTools.ofLazy(
    AsmRouteId.atp_exposedAccounts_detail_overview,
    () =>
      import('@asm/components-atp/components/atp/asm-atp-exposed-account-detail-page/asm-atp-exposed-account-overview-page/asm-atp-exposed-account-overview-page.component').then(
        m => m.AsmAtpExposedAccountOverviewPageComponent,
      ),
    [AsmAuthority.MODULE_ACCOUNT_TAKEOVER_PREVENTION, AsmAuthority.VIEW_ATP],
  ),

  // /atp/exposed-accounts/:id/scoring
  RouterTools.ofLazy(
    AsmRouteId.atp_exposedAccounts_detail_scoring,
    () =>
      import('@asm/components-atp/components/atp/asm-atp-exposed-account-detail-page/asm-atp-exposed-account-score-page/asm-atp-exposed-account-score-page.component').then(
        m => m.AsmAtpExposedAccountScorePageComponent,
      ),
    [AsmAuthority.MODULE_ACCOUNT_TAKEOVER_PREVENTION, AsmAuthority.VIEW_ATP],
  ),

  // /atp/exposed-accounts/:id/activity
  RouterTools.ofLazy(
    AsmRouteId.atp_exposedAccounts_detail_activity,
    () =>
      import('@asm/components-atp/components/atp/asm-atp-exposed-account-detail-page/asm-atp-exposed-account-activity-page/asm-atp-exposed-account-activity-page.component').then(
        m => m.AsmAtpExposedAccountActivityPageComponent,
      ),
    [AsmAuthority.MODULE_ACCOUNT_TAKEOVER_PREVENTION, AsmAuthority.VIEW_ATP],
  ),

  // /atp/exposure-artifacts
  RouterTools.ofLazy(
    AsmRouteId.atp_exposureArtifacts,
    () =>
      import('@asm/components-atp/components/atp/asm-atp-exposure-artifacts-page/asm-atp-exposure-artifacts-page.component').then(
        m => m.AsmAtpExposureArtifactsPageComponent,
      ),
    [AsmAuthority.MODULE_ACCOUNT_TAKEOVER_PREVENTION, AsmAuthority.VIEW_ATP],
  ),

  // /atp/exposure-artifacts/:id → /atp/exposure-artifacts/:id/overview
  RouterTools.ofRedirect(AsmRouteId.atp_exposureArtifacts_detail, AsmRouteId.atp_exposureArtifacts_detail_overview),

  // /atp/exposure-artifacts/:id/overview
  RouterTools.ofLazy(
    AsmRouteId.atp_exposureArtifacts_detail_overview,
    () =>
      import('@asm/components-atp/components/atp/asm-atp-exposure-set-detail-page/asm-atp-exposure-set-overview-page/asm-atp-exposure-set-overview-page.component').then(
        m => m.AsmAtpExposureSetOverviewPageComponent,
      ),
    [AsmAuthority.MODULE_ACCOUNT_TAKEOVER_PREVENTION, AsmAuthority.VIEW_ATP],
  ),

  // /atp/exposure-artifacts/:id/scoring
  RouterTools.ofLazy(
    AsmRouteId.atp_exposureArtifacts_detail_scoring,
    () =>
      import('@asm/components-atp/components/atp/asm-atp-exposure-set-detail-page/asm-atp-exposure-set-score-page/asm-atp-exposure-set-score-page.component').then(
        m => m.AsmAtpExposureSetScorePageComponent,
      ),
    [AsmAuthority.MODULE_ACCOUNT_TAKEOVER_PREVENTION, AsmAuthority.VIEW_ATP],
  ),

  // /atp/exposure-artifacts/:id/activity
  RouterTools.ofLazy(
    AsmRouteId.atp_exposureArtifacts_detail_activity,
    () =>
      import('@asm/components-atp/components/atp/asm-atp-exposure-set-detail-page/asm-atp-exposure-set-activity-page/asm-atp-exposure-set-activity-page.component').then(
        m => m.AsmAtpExposureSetActivityPageComponent,
      ),
    [AsmAuthority.MODULE_ACCOUNT_TAKEOVER_PREVENTION, AsmAuthority.VIEW_ATP],
  ),

  // /atp/exposed-cookies
  RouterTools.ofLazy(
    AsmRouteId.atp_exposedCookies,
    () =>
      import('@asm/components-atp/components/atp/asm-atp-exposed-cookies-page/asm-atp-exposed-cookies-page.component').then(
        m => m.AsmAtpExposedCookiesPageComponent,
      ),
    [AsmAuthority.MODULE_ACCOUNT_TAKEOVER_PREVENTION, AsmAuthority.VIEW_ATP],
  ),

  // /atp/exposed-cookies/:id → /atp/exposed-cookies/:id/overview
  RouterTools.ofRedirect(AsmRouteId.atp_exposedCookies_detail, AsmRouteId.atp_exposedCookies_detail_overview),

  // /atp/exposed-cookies/:id/overview
  RouterTools.ofLazy(
    AsmRouteId.atp_exposedCookies_detail_overview,
    () =>
      import('@asm/components-atp/components/atp/asm-atp-exposed-cookie-detail-page/asm-atp-exposed-cookie-overview-page/asm-atp-exposed-cookie-overview-page.component').then(
        m => m.AsmAtpExposedCookieOverviewPageComponent,
      ),
    [AsmAuthority.MODULE_ACCOUNT_TAKEOVER_PREVENTION, AsmAuthority.VIEW_ATP],
  ),

  // /atp/exposed-cookies/:id/scoring
  RouterTools.ofLazy(
    AsmRouteId.atp_exposedCookies_detail_scoring,
    () =>
      import('@asm/components-atp/components/atp/asm-atp-exposed-cookie-detail-page/asm-atp-exposed-cookie-score-page/asm-atp-exposed-cookie-score-page.component').then(
        m => m.AsmAtpExposedCookieScorePageComponent,
      ),
    [AsmAuthority.MODULE_ACCOUNT_TAKEOVER_PREVENTION, AsmAuthority.VIEW_ATP],
  ),

  // /atp/exposed-cookies/:id/activity
  RouterTools.ofLazy(
    AsmRouteId.atp_exposedCookies_detail_activity,
    () =>
      import('@asm/components-atp/components/atp/asm-atp-exposed-cookie-detail-page/asm-atp-exposed-cookie-activity-page/asm-atp-exposed-cookie-activity-page.component').then(
        m => m.AsmAtpExposedCookieActivityPageComponent,
      ),
    [AsmAuthority.MODULE_ACCOUNT_TAKEOVER_PREVENTION, AsmAuthority.VIEW_ATP],
  ),
];
